import React, {
  ReactNode,
  useState,
  useRef,
  useEffect,
  createContext,
  useContext,
} from 'react';
import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import { css } from '@emotion/css';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import Link from '@amzn/meridian/link';
import Box from '@amzn/meridian/box';
import useControllableState from '../hooks/useControllableState';

const iconStyle = css({
  transition: 'transform 0.1s ease',
});

const rotateDown = css({
  transform: 'rotate(0deg)',
});

const rotateRight = css({
  transform: 'rotate(-90deg)',
});

const expandableContent = css({
  overflow: 'hidden',
  transition: 'height 0.1s ease',
});

/* Why does this custom component exist instead of using the default Merdian expander?
The answer to this is the default Meridian expander does not support UX's designs for our coaching stats + outlier cards components.
The default expander only supports a single line of unexpanded content, and does not easily allow equal heights of sibling expanders 
https://meridian.a2z.com/components/expander/?platform=react-web
*/

interface ExpandableCardProps {
  title: ReactNode;
  header?: ReactNode;
  expandedContent: ReactNode;
  footer?: ReactNode;
  titleAddons?: ReactNode;
  cardSpacingInset?: string;
  titleRowSpacing?: string;
  showDivider?: boolean;
  clickstreamTags?: any;
  showChevron?: boolean;
  expanded?: boolean; // When provided, component is controlled
  onExpandedChange?: (expanded: boolean) => void;
  // Uncontrolled props
  defaultExpanded?: boolean; // Default state for uncontrolled mode
}

const ExpandableCard = ({
  title,
  header,
  expandedContent,
  footer,
  titleAddons,
  cardSpacingInset = '400 450 450',
  titleRowSpacing = 'none 300 300 300',
  showDivider = true,
  clickstreamTags = {},
  showChevron = true,
  defaultExpanded = false,
  expanded,
  onExpandedChange,
}: ExpandableCardProps) => {
  const contentRef = useRef<HTMLDivElement>();
  const expandedContentRef = useRef<HTMLDivElement>();

  const [isExpanded, setIsExpanded] = useControllableState(
    expanded,
    defaultExpanded,
    onExpandedChange
  );

  const { maxExpandedSiblingHeight, updateMaxHeight } = useContext(
    ExpandableCardGroupContext
  );

  const handleClick = () => setIsExpanded(!isExpanded);

  // if we're inside of a ExpandableCardGroupProvider, expand to max of sibiling height. otherwise just expand to fit the current content
  const [expandedHeight, setExpandedHeight] = useState(
    contentRef.current?.scrollHeight
  );

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      setExpandedHeight(contentRef.current?.scrollHeight);
    });

    if (expandedContentRef.current) {
      resizeObserver.observe(expandedContentRef.current);
    }
  }, []);

  const heightToExpandTo = maxExpandedSiblingHeight ?? expandedHeight;
  const expandedContentHeight = isExpanded ? heightToExpandTo : 0;

  useEffect(() => {
    if (contentRef.current) {
      updateMaxHeight(expandedHeight ?? 0);
    }
  }, [expandedHeight, updateMaxHeight]);

  return (
    <Card
      spacingInset={cardSpacingInset}
      data-testid={
        isExpanded ? 'expandable-card-expanded' : 'expandable-card-closed'
      }
    >
      <Column spacing='none' spacingInset='none'>
        <Row
          spacingInset={titleRowSpacing}
          spacing='400'
          width='fill'
          widths={['fit', 'fill']}
          alignmentHorizontal='justify'
          alignmentVertical='center'
        >
          <Row spacing='400' alignmentVertical='center'>
            {showChevron && (
              <Icon
                data-testid='expandable-card-icon'
                className={`${iconStyle} ${
                  isExpanded ? rotateDown : rotateRight
                }`}
                tokens={chevronDownSmallTokens}
              />
            )}
            <Link
              data-testid='expandable-card-title'
              type='secondary'
              {...clickstreamTags}
              onClick={handleClick}
            >
              {title}
            </Link>
          </Row>
          {titleAddons && <Box>{titleAddons}</Box>}
        </Row>

        {header}

        <div
          data-testid='expandable-content'
          className={expandableContent}
          style={{ height: `${expandedContentHeight}px` }}
        >
          <Column
            ref={contentRef}
            height='100%'
            heights={['fit', 'fill', 'fit']}
          >
            {showDivider && (
              <Box>
                <Divider />
              </Box>
            )}
            <Box ref={expandedContentRef}>{expandedContent}</Box>
            {footer && <Box>{footer}</Box>}
          </Column>
        </div>
      </Column>
    </Card>
  );
};

interface ExpandableCardGroupContextType {
  maxExpandedSiblingHeight?: number;
  updateMaxHeight: (height: number) => void;
}

const ExpandableCardGroupContext =
  createContext<ExpandableCardGroupContextType>({
    updateMaxHeight: () => {},
  });

// Optionally wrap multiple <ExpandableCard/> s with ExpandableCardGroupProvider to make all expanded card the same height.
export const ExpandableCardGroupProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [maxExpandedSiblingHeight, setMaxExpandedHeight] = useState(0);

  const updateMaxHeight = (height: number) => {
    if (height > maxExpandedSiblingHeight) {
      setMaxExpandedHeight(height);
    }
  };

  return (
    <ExpandableCardGroupContext.Provider
      value={{ maxExpandedSiblingHeight, updateMaxHeight }}
    >
      {children}
    </ExpandableCardGroupContext.Provider>
  );
};

ExpandableCard.displayName = 'ExpandableCard';
export default ExpandableCard;
